import { useState } from 'react';
import {
  LoginModalState,
  BrokerSpecificComponentsProps,
  UserValidaitionStateList,
} from 'v3/pages/LandingPage/Default/BannerSection/UserAuth/LoginModal/types.type';
import {
  sendOtpToBrokerUser,
  validateOtpToBrokerUser,
  validateBrokerUser,
  validatePinToBrokerUser,
} from 'v3/common/api/user';
import { cleanObject, onReDirectPage } from '../config';
import { reactToastify, TOASTIFY_TYPES } from 'v3/common/utils/reactToastify';
import { useResponsive } from 'v3/common/hooks/useResponsive';

type UserValidaitionProps = {
  selectedBrokerConfig: LoginModalState['selectedBrokerConfig'];
};

export const useUserValidation = ({ selectedBrokerConfig }: UserValidaitionProps) => {
  const [userValidaitionState, setUserValidaitionState] = useState<UserValidaitionStateList>({
    clientId: '',
    password: '',
    apiKey: '',
    secretKey: '',
    otp: '',
    totp: '',
    brokerVariant: '',
    mobileNumber: null,
    isOtpSent: false,
    isClientValidated: false,
    isOtpValidated: false,
    isPinValidated: false,
    shouldShowApiKey: false,
    shouldShowSecretKey: false,
    isValidForm: true,
    brokerErrorMessage: '',
    brokerExtras: {},
    isLoading: false,
    selectedBrokerConfig: selectedBrokerConfig,
    token: '',
    yob: null,
    pin: '',
    ipAddress: '',
  });

  const { apiKey, secretKey, clientId, password, otp, mobileNumber, totp, pin, token, ipAddress } =
    userValidaitionState;

  const updatePartialConfigs = (partialConfigs: Partial<UserValidaitionStateList>) =>
    setUserValidaitionState({
      ...userValidaitionState,
      ...partialConfigs,
    });

  const validateClientId: BrokerSpecificComponentsProps['brokerSpecificHandles']['validateClientId'] = (
    args
  ) => {
    updatePartialConfigs({ isLoading: true });
    validateBrokerUser(
      cleanObject({
        broker_user_id: clientId,
        broker: selectedBrokerConfig?.id,
        mobile_number: mobileNumber,
        ...args,
      })
    ).then((result) => {
      const { status, broker_api_credentials: brokerApiCredientals, broker_raw_info } = result;
      if (!status)
        return updatePartialConfigs({
          shouldShowApiKey: true,
          shouldShowSecretKey: true,
          isClientValidated: true,
          isLoading: false,
          isValidForm: true,
        });
      updatePartialConfigs({
        apiKey: brokerApiCredientals?.api_key,
        secretKey: brokerApiCredientals?.api_secret,
        shouldShowSecretKey: !brokerApiCredientals?.api_secret,
        brokerExtras: broker_raw_info,
        isClientValidated: true,
        isLoading: false,
        isValidForm: true,
      });
    });
  };

  const sendOtp: BrokerSpecificComponentsProps['brokerSpecificHandles']['sendOtp'] = (
    isResendOtp = false
  ) => {
    updatePartialConfigs({ isLoading: true, brokerErrorMessage: '' });
    sendOtpToBrokerUser(
      cleanObject({
        client_id: clientId,
        mobile_number: mobileNumber,
        password,
        otp,
        totp,
        api_key: apiKey,
        api_secret: secretKey,
      }),
      isResendOtp,
      selectedBrokerConfig?.id ?? ''
    )
      .then((res) => {
        const { status, message } = res;
        const extras = res?.extras;
        if (!status) {
          reactToastify(message, TOASTIFY_TYPES.ERROR);
          updatePartialConfigs({ brokerErrorMessage: message });
        } else {
          reactToastify('Your OTP has been sent!', TOASTIFY_TYPES.SUCCESS);
          updatePartialConfigs({
            isOtpSent: true,
            brokerErrorMessage: '',
            brokerExtras: extras,
            isLoading: false,
          });
        }
      })
      .catch(() => {});
  };

  const reDirectPage: BrokerSpecificComponentsProps['brokerSpecificHandles']['reDirectPage'] = (
    params = {},
    toCallBack = true
  ) => {
    if (!selectedBrokerConfig) return updatePartialConfigs({ isValidForm: false });
    updatePartialConfigs({ isLoading: true });

    onReDirectPage(
      cleanObject({
        broker: selectedBrokerConfig.id,
        client_id: clientId,
        password,
        api_key: apiKey,
        api_secret: secretKey,
        mobile_number: mobileNumber,
        ip_address: ipAddress,
        ...params,
      }),
      selectedBrokerConfig.id,
      toCallBack,
      () => {
        updatePartialConfigs({ isLoading: false });
      }
    );
  };

  const validateOtp: BrokerSpecificComponentsProps['brokerSpecificHandles']['validateOtp'] = (
    params,
    toCallBack = false
  ) => {
    updatePartialConfigs({ isLoading: true });
    validateOtpToBrokerUser(
      cleanObject({
        client_id: clientId,
        password,
        otp,
        totp,
        mobile_number: mobileNumber,
        api_key: apiKey,
        api_secret: secretKey,
        ...params,
      }),
      selectedBrokerConfig?.id ?? ''
    )
      .then((res) => {
        const { status, token, message, ...rest } = res;
        if (!status) {
          reactToastify(message, TOASTIFY_TYPES.ERROR);
          updatePartialConfigs({ brokerErrorMessage: message });
        } else {
          reDirectPage({ client_id: clientId, token, ...rest }, toCallBack);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        updatePartialConfigs({ isLoading: false });
      });
  };

  const validateTotp: BrokerSpecificComponentsProps['brokerSpecificHandles']['validateTotp'] = (
    params,
    shouldRedirect
  ) => {
    updatePartialConfigs({ isLoading: true, brokerErrorMessage: '' });
    validateOtpToBrokerUser(
      cleanObject({
        client_id: clientId,
        mobile_number: mobileNumber,
        password,
        otp: otp,
        totp: totp,
        api_key: apiKey,
        api_secret: secretKey,
        token: token,
        ...params,
      }),
      selectedBrokerConfig?.id ?? ''
    )
      .then((res) => {
        const { status, message, token, ...rest } = res;
        if (!status) {
          reactToastify(message, TOASTIFY_TYPES.ERROR);
          updatePartialConfigs({ brokerErrorMessage: message });
        } else {
          reactToastify('Your TOTP has been validated!', TOASTIFY_TYPES.SUCCESS);
          updatePartialConfigs({
            brokerErrorMessage: '',
            brokerExtras: rest,
            isLoading: false,
            isOtpValidated: true,
            token: token,
          });
          if (shouldRedirect) {
            reDirectPage({ client_id: clientId, token, ...rest }, false);
          }
        }
      })
      .catch((e) => {
        reactToastify(e.message, TOASTIFY_TYPES.ERROR);
      });
  };

  const validatePin: BrokerSpecificComponentsProps['brokerSpecificHandles']['validatePin'] = (
    params,
    shouldRedirect
  ) => {
    updatePartialConfigs({ isLoading: true, brokerErrorMessage: '' });
    validatePinToBrokerUser(
      cleanObject({
        client_id: clientId,
        mobile_number: mobileNumber,
        password,
        pin: pin,
        otp: otp,
        api_key: apiKey,
        api_secret: secretKey,
        token: token,
        ...params,
      }),
      selectedBrokerConfig?.id ?? ''
    )
      .then((res) => {
        const { status, message, token, ...rest } = res;
        if (!status) {
          reactToastify(message, TOASTIFY_TYPES.ERROR);
          updatePartialConfigs({ brokerErrorMessage: message });
        } else {
          reactToastify('Your Pin has been validated!', TOASTIFY_TYPES.SUCCESS);
          updatePartialConfigs({
            isOtpSent: true,
            brokerErrorMessage: '',
            brokerExtras: rest,
            isLoading: false,
            isPinValidated: true,
            token: token,
          });
          if (shouldRedirect) {
            reDirectPage({ client_id: clientId, ip_address: ipAddress, token, ...rest }, false);
          }
        }
      })
      .catch((e) => {
        reactToastify(e.message, TOASTIFY_TYPES.ERROR);
      });
  };

  const changeApiKey: BrokerSpecificComponentsProps['brokerSpecificHandles']['changeApiKey'] = () => {
    updatePartialConfigs({ shouldShowApiKey: true, shouldShowSecretKey: true, isClientValidated: true });
  };

  const validateClientIdAndRedirect: BrokerSpecificComponentsProps['brokerSpecificHandles']['validateClientIdAndRedirect'] =
    async (args) => {
      updatePartialConfigs({ isLoading: true });
      validateBrokerUser({
        broker_user_id: clientId,
        broker: selectedBrokerConfig?.id,
        mobile_number: mobileNumber,
        ...args,
      }).then((result) => {
        const { status, broker_api_credentials: brokerApiCredientals } = result;

        if (!status) {
          return updatePartialConfigs({
            shouldShowApiKey: true,
            shouldShowSecretKey: true,
            isClientValidated: true,
            isLoading: false,
            isValidForm: true,
          });
        }
        if (brokerApiCredientals?.api_key && brokerApiCredientals?.api_secret) {
          reDirectPage(
            {
              api_key: brokerApiCredientals.api_key,
              api_secret: brokerApiCredientals.api_secret,
            },
            false
          );
        }
      });
    };

  const isMobile: boolean = useResponsive();

  return {
    userValidaitionState,
    brokerSpecificHandles: {
      updatePartialConfigs,
      validateClientId,
      sendOtp,
      reDirectPage,
      validateOtp,
      changeApiKey,
      validateClientIdAndRedirect,
      isMobile,
      validateTotp,
      validatePin,
    },
  };
};
